import { ArrowRight, Phone, UserPlus, CheckCircle2 } from "lucide-react";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogTrigger } from "./ui/dialog";
import { useState } from "react";
import ContactForm from "./ContactForm";
import heroImage from "../assets/hero-luxury-building.jpg";
import { useNavigate } from "react-router-dom";
import TypeWriter from "./Handlers/Typewriter";
import CountUp from "react-countup";
import "./styles.css";
import { useInView } from "react-intersection-observer";


const API_URL = window.location.href.includes("localhost")
  ? process.env.REACT_APP_API_URL_LOCAL
  : process.env.REACT_APP_API_URL_PROD;


const Hero = () => {
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const navigate = useNavigate();

  const handleCall = () => {
    window.location.href = "tel:+91-9373733473";
  };

  const { ref: counterRef, inView: countersInView } = useInView({
    triggerOnce: true, // only animate once
    threshold: 0.3, // start when 30% visible
  });

  // API Call
  const handleQuickRegister = async (formData) => {
    setLoading(true);
    setSuccessMsg("");
    try {
      const res = await fetch(
        `${API_URL}api/global-form/skyline-properties/quick-registration`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(formData),
        }
      );

      if (!res.ok) throw new Error("Something went wrong");

      setSuccessMsg(
        "🎉 Thank you! Your details are registered. Our team will call you within 2 hours."
      );
      setShowForm(false);
    } catch (err) {
      setSuccessMsg("⚠️ There was an issue submitting the form. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const typeWriter = ['luxury', 'modern', 'exclusive', 'dream']

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 z-0">
        <img
          src={heroImage}
          alt="Luxury residential development with modern architecture"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-black/80 via-black/70 to-black/50" />
      </div>

      {/* Content */}
      <div className="relative z-10 container mx-auto px-6 text-center text-white">
        <div className="max-w-5xl mx-auto space-y-10">
          {/* Headline */}
          <div className="space-y-6 animate-fade-up mt-10">
            <h1 className="font-display-bold leading-tight tracking-tight">
              <span className="block text-luxury animate-slide-up text-5xl md:text-7xl lg:text-8xl">
                Skyline Properties
              </span>
              <p className="text-2xl md:text-3xl lg:text-4xl mt-4 text-white/80 font-medium leading-snug">
                Explore{" "}
                <span className="text-luxury bg-clip-text text-transparent 
                  bg-gradient-to-r from-green-400 via-blue-500 to-pink-500 
                  font-bold animate-type">
                  <TypeWriter data={typeWriter} />
                </span>{" "}
                <br />
homes designed for inspired living.              </p>
            </h1>

            {/* Subline */}
            <div className="mt-10" style={{ marginTop: "10rem" }}>
              <p className="text-lg md:text-2xl text-white/90 max-w-3xl mx-auto font-light">
Your lifestyle deserves a masterpiece —           <span className="font-semibold text-luxury"> discover it here     </span>
              </p>
            </div>
          </div>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center animate-fade-up">
            <Dialog>
              <DialogTrigger asChild>
                <Button
                  variant="outline"
                  size="lg"
                  className="btn-outline-luxury group shadow-xl hover:shadow-2xl"
                  onClick={() => {
                    setShowForm(false);
                    setSuccessMsg("");
                  }}
                >
                  Let’s Talk Business
                  <Phone className="ml-2 h-5 w-5 group-hover:scale-110 transition-transform" />
                </Button>
              </DialogTrigger>

              <DialogContent className="max-w-md rounded-2xl shadow-2xl" style={{ width: "95%" }}>
                <div className="text-center space-y-6 p-6">
                  <h3 className="font-display-bold text-3xl text-primary">
                    Let’s Get Started
                  </h3>

                  {successMsg && (
                    <div className="p-4 bg-green-100 text-green-700 rounded-lg flex items-center gap-2">
                      <CheckCircle2 className="h-5 w-5 text-green-600" />
                      <span>{successMsg}</span>
                    </div>
                  )}

                  {!successMsg && !showForm && (
                    <>
                      <p className="text-muted-foreground">
                        Choose your preferred way to connect with us
                      </p>
                      <div className="flex flex-col gap-4">
                        <Button size="lg" className="btn-luxury group" onClick={handleCall}>
                          <Phone className="mr-2 h-5 w-5" />
                          Call Now
                        </Button>
                        <Button
                          variant="outline"
                          size="lg"
                          onClick={() => setShowForm(true)}
                          className="group"
                          style={{ border: "0.1px solid #d4d4d4" }}
                        >
                          <UserPlus className="mr-2 h-5 w-5" />
                          Register Interest
                        </Button>
                      </div>
                    </>
                  )}

                  {!successMsg && showForm && (
                    <div className="mt-6 pt-6 border-t border-border">
                      <ContactForm
                        compact
                        onSubmit={(data) => handleQuickRegister(data)}
                        loading={loading}
                      />
                    </div>
                  )}
                </div>
              </DialogContent>
            </Dialog>

            <Button
  size="lg"
  className="
    relative group px-10 py-4 text-lg font-extrabold rounded-2xl 
    bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 
    text-white 
    shadow-[0_0_25px_rgba(255,215,0,0.5)] 
    hover:shadow-[0_0_50px_rgba(255,215,0,0.8)] 
    overflow-hidden 
    transition-all duration-500 ease-in-out
  "
  onClick={() => navigate("/explore-properties")}
>
  <span className="relative z-10 flex items-center justify-center">
    Explore Now
    <ArrowRight className="ml-3 h-6 w-6 transition-transform duration-500 group-hover:translate-x-3 rotate-0 group-hover:rotate-12" />
  </span>

  {/* Animated shimmer effect */}
  <span className="
    absolute top-0 left-0 w-full h-full
    bg-white opacity-10
    blur-xl
    animate-[shine_2s_infinite]
    pointer-events-none
  "></span>

  <style jsx>{`
    @keyframes shine {
      0% { transform: translateX(-100%) rotate(0deg); }
      50% { transform: translateX(100%) rotate(10deg); }
      100% { transform: translateX(100%) rotate(10deg); }
    }
  `}</style>
</Button>

          </div>

          {/* Trust Badges with Animated Counters */}
           <div ref={counterRef} className="pt-10 animate-fade-up">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 max-w-4xl mx-auto">
          
          <div className="text-center">
            <div className="text-4xl md:text-5xl font-display-bold text-luxury mb-3">
              {countersInView ? <CountUp start={0} end={20000} duration={7} separator="," /> : "0"}+
            </div>
            <div className="text-white/80 text-lg">Happy Clients</div>
          </div>
          
          <div className="text-center">
            <div className="text-4xl md:text-5xl font-display-bold text-luxury mb-3">
              {countersInView ? <CountUp start={0} end={200} duration={5} separator="," /> : "0"}+
            </div>
            <div className="text-white/80 text-lg">Premium Properties Sold</div>
          </div>
          
          <div className="text-center">
            <div className="text-4xl md:text-5xl font-display-bold text-luxury mb-3">
              {countersInView ? <CountUp start={0} end={50} duration={10} separator="," /> : "0"}+
            </div>
            <div className="text-white/80 text-lg">Properties Sold Annually</div>
          </div>

        </div>
      </div>
        </div>
        <br />
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center">
          <div className="w-1 h-3 bg-white/70 rounded-full mt-2 animate-pulse" />
        </div>
      </div>
    </section>
  );
};

export default Hero;
